import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from './Layout';
import api from '../services/api';
import { Lead, LeadStage } from '../types/Lead';
import { useBranch } from '../contexts/BranchContext';
import {
  UserIcon,
  PhoneIcon,
  EnvelopeIcon,
  BuildingOfficeIcon,
  PlusIcon,
  EyeIcon,
  ArrowUpTrayIcon
} from '@heroicons/react/24/outline';

import { CustomerSegment } from '../types/CustomerSegment';
import BulkLeadImportModal from './BulkLeadImportModal';

const LeadsPipeline: React.FC = () => {
  const navigate = useNavigate();
  const { currentBranch, isViewingAllBranches } = useBranch();
  const [viewMode, setViewMode] = useState<'stages' | 'segments'>('stages');
  const [stages, setStages] = useState<LeadStage[]>([]);
  const [segments, setSegments] = useState<CustomerSegment[]>([]);
  const [leadsByStage, setLeadsByStage] = useState<{ [key: number]: Lead[] }>({});
  const [leadsBySegment, setLeadsBySegment] = useState<{ [key: number]: Lead[] }>({});
  const [loading, setLoading] = useState(true);
  const [draggedLead, setDraggedLead] = useState<Lead | null>(null);
  const [dragOverStage, setDragOverStage] = useState<number | null>(null);
  const [dragOverSegment, setDragOverSegment] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showBulkImportModal, setShowBulkImportModal] = useState(false);
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    loadPipelineData();
  }, [currentBranch]);

  const loadPipelineData = async () => {
    setLoading(true);
    try {
      // Load stages and segments
      const [stagesRes, segmentsRes, leadsRes] = await Promise.all([
        api.get('/leads/stages?withSegments=true'),
        api.get('/customer-segments'),
        api.get('/leads?limit=1000&sortBy=created_at&sortOrder=DESC')
      ]);

      const stagesData = stagesRes.data?.data || [];
      const activeStages = stagesData.filter((s: LeadStage) => s.isActive);
      setStages(activeStages);

      const segmentsData = segmentsRes.data?.data || [];
      setSegments(segmentsData);

      const allLeads = leadsRes.data?.data || [];

      // Group leads by stage
      const groupedByStage: { [key: number]: Lead[] } = {};
      activeStages.forEach((stage: LeadStage) => {
        groupedByStage[stage.id] = allLeads.filter((lead: Lead) => lead.stageId === stage.id);
      });
      setLeadsByStage(groupedByStage);

      // Group leads by segment
      const groupedBySegment: { [key: number]: Lead[] } = {};
      segmentsData.forEach((segment: CustomerSegment) => {
        const leadsInSegment = allLeads.filter((lead: Lead) => {
          // Check if lead has this segment
          if (lead.segmentIds && Array.isArray(lead.segmentIds) && lead.segmentIds.includes(segment.id)) {
            return true;
          }
          // Also check segments array
          if (lead.segments && Array.isArray(lead.segments)) {
            return lead.segments.some(s => s.id === segment.id);
          }
          return false;
        });
        groupedBySegment[segment.id] = leadsInSegment;
      });
      
      console.log('Grouped by segment:', groupedBySegment);
      console.log('Sample lead segmentIds:', allLeads[0]?.segmentIds);
      console.log('Sample lead segments:', allLeads[0]?.segments);
      
      setLeadsBySegment(groupedBySegment);

      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load pipeline data');
    } finally {
      setLoading(false);
    }
  };

  const handleDragStart = (lead: Lead) => {
    setDraggedLead(lead);
  };

  const handleDragOver = (e: React.DragEvent, stageId: number) => {
    e.preventDefault();
    setDragOverStage(stageId);
  };

  const handleDragLeave = () => {
    setDragOverStage(null);
  };

  const handleDrop = async (e: React.DragEvent, newStageId: number) => {
    e.preventDefault();
    setDragOverStage(null);

    if (!draggedLead || draggedLead.stageId === newStageId) {
      setDraggedLead(null);
      return;
    }

    const oldStageId = draggedLead.stageId;

    // Optimistic update
    const updatedLeadsByStage = { ...leadsByStage };
    if (oldStageId) {
      updatedLeadsByStage[oldStageId] = updatedLeadsByStage[oldStageId]?.filter(l => l.id !== draggedLead.id) || [];
    }
    updatedLeadsByStage[newStageId] = [...(updatedLeadsByStage[newStageId] || []), draggedLead];
    setLeadsByStage(updatedLeadsByStage);

    // Update backend
    setUpdating(true);
    try {
      await api.put(`/leads/${draggedLead.id}`, { stageId: newStageId });
      
      // Reload to get fresh data
      await loadPipelineData();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update lead stage');
      // Revert on error
      await loadPipelineData();
    } finally {
      setUpdating(false);
      setDraggedLead(null);
    }
  };

  if (loading) {
    return (
      <Layout title="Pipeline" subtitle="Loading...">
        <div className="flex items-center justify-center py-12">
          <div>
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading pipeline...</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout title="Lead Pipeline" subtitle="Visual pipeline management">
      <div className="flex flex-col h-[calc(100vh-6rem)] overflow-hidden">
        {/* Header */}
        <div className="flex-shrink-0 flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Pipeline Board</h2>
            <p className="text-gray-600 mt-1">
              {viewMode === 'stages' ? 'Drag and drop leads between stages' : 'Organized by customer segments'}
            </p>
          </div>
          <div className="flex items-center space-x-3">
            {/* View Mode Toggle */}
            <div className="flex items-center bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => setViewMode('stages')}
                className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                  viewMode === 'stages'
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                📊 By Stage
              </button>
              <button
                onClick={() => setViewMode('segments')}
                className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                  viewMode === 'segments'
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                🎯 By Segment
              </button>
            </div>
            <button
              onClick={() => navigate('/leads')}
              className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 flex items-center space-x-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
              </svg>
              <span>List View</span>
            </button>
            <button
              onClick={() => setShowBulkImportModal(true)}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center space-x-2"
            >
              <ArrowUpTrayIcon className="h-5 w-5" />
              <span>Bulk Import</span>
            </button>
            <button
              onClick={() => navigate('/leads/new')}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center space-x-2"
            >
              <PlusIcon className="h-5 w-5" />
              <span>Add Lead</span>
            </button>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="flex-shrink-0 bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg">
            {error}
          </div>
        )}

        {/* Updating Overlay */}
        {updating && (
          <div className="fixed top-4 right-4 bg-blue-600 text-white px-4 py-2 rounded-lg shadow-lg z-50">
            Updating...
          </div>
        )}

        {/* Kanban Board - only scrollable area */}
        <div className="flex-1 min-h-0 flex gap-4 overflow-x-auto overflow-y-hidden pb-2">
          {viewMode === 'stages' ? (
            // Stage-wise view
            stages.map((stage) => {
              const leads = leadsByStage[stage.id] || [];
              const isOver = dragOverStage === stage.id;

            return (
              <div
                key={stage.id}
                className={`flex-shrink-0 w-80 h-full flex flex-col bg-gray-50 rounded-lg transition-all ${
                  isOver ? 'ring-2 ring-blue-500 bg-blue-50' : ''
                }`}
                onDragOver={(e) => handleDragOver(e, stage.id)}
                onDragLeave={handleDragLeave}
                onDrop={(e) => handleDrop(e, stage.id)}
              >
                {/* Stage Header */}
                <div 
                  className="flex-shrink-0 p-4 border-b border-gray-200 bg-white rounded-t-lg"
                  style={{ borderTopColor: stage.color, borderTopWidth: '4px' }}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <div
                        className="w-3 h-3 rounded-full"
                        style={{ backgroundColor: stage.color }}
                      />
                      <h3 className="font-semibold text-gray-900">{stage.name}</h3>
                    </div>
                    <span className="px-2 py-1 bg-gray-200 text-gray-700 text-xs font-semibold rounded-full">
                      {leads.length}
                    </span>
                  </div>
                  {stage.segmentCodes && !stage.isGlobal && (
                    <p className="text-xs text-purple-600 mt-1">
                      {stage.segmentCodes}
                    </p>
                  )}
                  {stage.isGlobal && (
                    <p className="text-xs text-blue-600 mt-1">Global Stage</p>
                  )}
                </div>

                {/* Leads List */}
                <div className="flex-1 min-h-0 p-3 space-y-3 overflow-y-auto">
                  {leads.length === 0 ? (
                    <div className="text-center py-8 text-gray-400 text-sm">
                      No leads in this stage
                    </div>
                  ) : (
                    leads.map((lead) => (
                      <div
                        key={lead.id}
                        draggable
                        onDragStart={() => handleDragStart(lead)}
                        className={`p-3 rounded-xl cursor-move transition-all bg-white/60 backdrop-blur-md border border-white/50 shadow-lg shadow-black/5 hover:bg-white/70 hover:shadow-xl hover:shadow-black/10 ${
                          draggedLead?.id === lead.id ? 'opacity-50' : ''
                        }`}
                      >
                        {/* Lead Card */}
                        <div className="space-y-2">
                          {/* Name */}
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <h4 className="font-semibold text-gray-900 text-sm">{lead.fullName}</h4>
                              {lead.leadUid && (
                                <p className="text-xs font-mono text-gray-500">{lead.leadUid}</p>
                              )}
                            </div>
                            <button
                              onClick={() => {
                                try {
                                  sessionStorage.setItem('leadshive_last_page', window.location.pathname + window.location.search);
                                } catch (_) {}
                                navigate(`/leads/${lead.id}`);
                              }}
                              className="text-blue-600 hover:text-blue-800 ml-2"
                              title="View Details"
                            >
                              <EyeIcon className="h-4 w-4" />
                            </button>
                          </div>

                          {/* Segments */}
                          {lead.segments && lead.segments.length > 0 && (
                            <div className="flex flex-wrap gap-1">
                              {lead.segments.slice(0, 2).map((segment) => (
                                <span
                                  key={segment.id}
                                  className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium text-white"
                                  style={{ backgroundColor: segment.color }}
                                >
                                  {segment.code}
                                </span>
                              ))}
                              {lead.segments.length > 2 && (
                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-200 text-gray-700">
                                  +{lead.segments.length - 2}
                                </span>
                              )}
                            </div>
                          )}

                          {/* Contact Info */}
                          <div className="space-y-1">
                            {lead.email && (
                              <div className="flex items-center text-xs text-gray-600">
                                <EnvelopeIcon className="h-3 w-3 mr-1" />
                                <span className="truncate">{lead.email}</span>
                              </div>
                            )}
                            {lead.phone && (
                              <div className="flex items-center text-xs text-gray-600">
                                <PhoneIcon className="h-3 w-3 mr-1" />
                                <span>{lead.phone}</span>
                              </div>
                            )}
                            {lead.company && (
                              <div className="flex items-center text-xs text-gray-600">
                                <BuildingOfficeIcon className="h-3 w-3 mr-1" />
                                <span className="truncate">{lead.company}</span>
                              </div>
                            )}
                          </div>

                          {/* Metadata */}
                          <div className="flex items-center justify-between pt-2 border-t border-white/40">
                            {lead.assigned_first_name && lead.assigned_last_name ? (
                              <div className="flex items-center text-xs text-gray-500">
                                <UserIcon className="h-3 w-3 mr-1" />
                                <span className="truncate">{lead.assigned_first_name} {lead.assigned_last_name}</span>
                              </div>
                            ) : (
                              <span className="text-xs text-gray-400 italic">Unassigned</span>
                            )}
                            
                            {lead.priority && (
                              <span className={`px-2 py-0.5 text-xs rounded-full ${
                                lead.priority === 'urgent' ? 'bg-red-100 text-red-800' :
                                lead.priority === 'high' ? 'bg-orange-100 text-orange-800' :
                                lead.priority === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                                'bg-green-100 text-green-800'
                              }`}>
                                {lead.priority.toUpperCase()}
                              </span>
                            )}
                          </div>

                          {/* Lead Value */}
                          {lead.leadValue && lead.leadValue > 0 && (
                            <div className="text-xs font-semibold text-green-600">
                              ${lead.leadValue.toLocaleString()}
                            </div>
                          )}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            );
          })
          ) : (
            // Segment-wise view
            segments.map((segment) => {
              const leads = leadsBySegment[segment.id] || [];
              const isOver = dragOverSegment === segment.id;

              return (
                <div
                  key={segment.id}
                  className="flex-shrink-0 w-80 h-full flex flex-col bg-gray-50 rounded-lg"
                >
                  {/* Segment Header */}
                  <div 
                    className="flex-shrink-0 p-4 border-b border-gray-200 bg-white rounded-t-lg"
                    style={{ borderTopColor: segment.color, borderTopWidth: '4px' }}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <div
                          className="w-3 h-3 rounded-full"
                          style={{ backgroundColor: segment.color }}
                        />
                        <h3 className="font-semibold text-gray-900">{segment.name}</h3>
                      </div>
                      <span className="px-2 py-1 bg-purple-100 text-purple-700 text-xs font-semibold rounded-full">
                        {leads.length}
                      </span>
                    </div>
                    <p className="text-xs text-gray-600 mt-1 font-mono">{segment.code}</p>
                  </div>

                  {/* Leads List */}
                  <div className="flex-1 min-h-0 p-3 space-y-3 overflow-y-auto">
                    {leads.length === 0 ? (
                      <div className="text-center py-8 text-gray-400 text-sm">
                        No leads in this segment
                      </div>
                    ) : (
                      leads.map((lead) => (
                        <div
                          key={lead.id}
                          draggable={false}
                          className="p-3 rounded-xl cursor-default transition-all bg-white/60 backdrop-blur-md border border-white/50 shadow-lg shadow-black/5 hover:bg-white/70 hover:shadow-xl hover:shadow-black/10"
                        >
                          {/* Same lead card as stage view */}
                          <div className="space-y-2">
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <h4 className="font-semibold text-gray-900 text-sm">{lead.fullName}</h4>
                                {lead.leadUid && (
                                  <p className="text-xs font-mono text-gray-500">{lead.leadUid}</p>
                                )}
                              </div>
                              <button
                                onClick={() => {
                                  try {
                                    sessionStorage.setItem('leadshive_last_page', window.location.pathname + window.location.search);
                                  } catch (_) {}
                                  navigate(`/leads/${lead.id}`);
                                }}
                                className="text-blue-600 hover:text-blue-800 ml-2"
                                title="View Details"
                              >
                                <EyeIcon className="h-4 w-4" />
                              </button>
                            </div>

                            {/* Show Stage in segment view */}
                            {lead.stage_name && (
                              <div>
                                <span 
                                  className="inline-flex px-2 py-0.5 text-xs font-semibold rounded-full text-white"
                                  style={{ backgroundColor: lead.stage_color }}
                                >
                                  {lead.stage_name}
                                </span>
                              </div>
                            )}

                            {/* Contact Info */}
                            <div className="space-y-1">
                              {lead.email && (
                                <div className="flex items-center text-xs text-gray-600">
                                  <EnvelopeIcon className="h-3 w-3 mr-1" />
                                  <span className="truncate">{lead.email}</span>
                                </div>
                              )}
                              {lead.phone && (
                                <div className="flex items-center text-xs text-gray-600">
                                  <PhoneIcon className="h-3 w-3 mr-1" />
                                  <span>{lead.phone}</span>
                                </div>
                              )}
                              {lead.company && (
                                <div className="flex items-center text-xs text-gray-600">
                                  <BuildingOfficeIcon className="h-3 w-3 mr-1" />
                                  <span className="truncate">{lead.company}</span>
                                </div>
                              )}
                            </div>

                            {/* Metadata */}
                            <div className="flex items-center justify-between pt-2 border-t border-white/40">
                              {lead.assigned_first_name && lead.assigned_last_name ? (
                                <div className="flex items-center text-xs text-gray-500">
                                  <UserIcon className="h-3 w-3 mr-1" />
                                  <span className="truncate">{lead.assigned_first_name} {lead.assigned_last_name}</span>
                                </div>
                              ) : (
                                <span className="text-xs text-gray-400 italic">Unassigned</span>
                              )}
                              
                              {lead.priority && (
                                <span className={`px-2 py-0.5 text-xs rounded-full ${
                                  lead.priority === 'urgent' ? 'bg-red-100 text-red-800' :
                                  lead.priority === 'high' ? 'bg-orange-100 text-orange-800' :
                                  lead.priority === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                                  'bg-green-100 text-green-800'
                                }`}>
                                  {lead.priority.toUpperCase()}
                                </span>
                              )}
                            </div>

                            {lead.leadValue && lead.leadValue > 0 && (
                              <div className="text-xs font-semibold text-green-600">
                                ${lead.leadValue.toLocaleString()}
                              </div>
                            )}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Thin stats bar fixed at deep bottom of viewport */}
        <div className="flex-shrink-0 h-0" aria-hidden="true" />
      </div>

      {/* Fixed to viewport bottom: compact stats bar (outside scroll flow) */}
      <div className="fixed bottom-0 left-0 right-0 z-20 border-t border-gray-200 bg-white/95 shadow-[0_-1px_6px_rgba(0,0,0,0.06)] pl-16 md:pl-64">
        <div className="px-4 py-1.5 flex items-center justify-center gap-6 text-[10px]">
          <span className="text-gray-500 uppercase tracking-wider">Total Leads</span>
          <span className="font-semibold text-gray-900 text-sm">
            {viewMode === 'stages'
              ? Object.values(leadsByStage).reduce((sum, leads) => sum + leads.length, 0)
              : Object.values(leadsBySegment).reduce((sum, leads) => sum + leads.length, 0)
            }
          </span>
          <span className="text-gray-400">|</span>
          <span className="text-gray-500 uppercase tracking-wider">{viewMode === 'stages' ? 'Stages' : 'Segments'}</span>
          <span className="font-semibold text-gray-900 text-sm">
            {viewMode === 'stages' ? stages.length : segments.length}
          </span>
          <span className="text-gray-400">|</span>
          <span className="text-gray-500 uppercase tracking-wider">Total Value</span>
          <span className="font-semibold text-green-600 text-sm">
            ${(viewMode === 'stages'
              ? Object.values(leadsByStage).flat()
              : Object.values(leadsBySegment).flat()
            )
              .reduce((sum, lead) => {
                const value = typeof lead.leadValue === 'number' ? lead.leadValue : parseFloat(String(lead.leadValue || 0));
                return sum + (isNaN(value) ? 0 : value);
              }, 0)
              .toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </span>
          <span className="text-gray-400">|</span>
          <span className="text-gray-500 uppercase tracking-wider">{viewMode === 'stages' ? 'Conversion' : 'Avg/Seg'}</span>
          <span className="font-semibold text-blue-600 text-sm">
            {viewMode === 'stages' ? (
              (() => {
                const wonStage = stages.find(s => s.name.toLowerCase() === 'won');
                const wonLeads = wonStage ? (leadsByStage[wonStage.id] || []).length : 0;
                const totalLeads = Object.values(leadsByStage).reduce((sum, leads) => sum + leads.length, 0);
                return totalLeads > 0 ? ((wonLeads / totalLeads) * 100).toFixed(1) : '0';
              })() + '%'
            ) : (
              Math.round(
                Object.values(leadsBySegment).reduce((sum, leads) => sum + leads.length, 0) /
                (segments.length || 1)
              )
            )}
          </span>
        </div>
        <p className="text-[10px] text-blue-700/80 text-center pb-1 px-2">
          💡 {viewMode === 'stages' ? 'Drag cards between stages · Click eye to view' : 'Segment view is read-only · Switch to By Stage to drag'}
        </p>
      </div>

      {/* Bulk Lead Import Modal */}
      <BulkLeadImportModal
        isOpen={showBulkImportModal}
        onClose={() => setShowBulkImportModal(false)}
        onSuccess={() => {
          setShowBulkImportModal(false);
          loadPipelineData();
        }}
      />
    </Layout>
  );
};

export default LeadsPipeline;
